import React from 'react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import {
  ArrowUp,
  ArrowDown,
  Trash,
  TextAlignLeft,
  Highlighter,
  Image as ImageIcon,
  Quotes,
  Link as LinkIcon,
} from '@phosphor-icons/react';

const BLOCK_TYPES = [
  { type: 'paragraph', label: 'Text', icon: TextAlignLeft },
  { type: 'highlight', label: 'Highlight', icon: Highlighter },
  { type: 'image', label: 'Image', icon: ImageIcon },
  { type: 'quote', label: 'Quote', icon: Quotes },
  { type: 'reference', label: 'Reference', icon: LinkIcon },
];

const emptyBlock = (type) => ({ type, text: '', url: '', caption: '', attribution: '', label: '' });

const textarea =
  'w-full px-3 py-2 rounded-lg border border-black/10 focus:border-primary focus:ring-1 focus:ring-primary text-sm';

export const BlockEditor = ({ blocks, onChange }) => {
  const update = (index, patch) =>
    onChange(blocks.map((b, i) => (i === index ? { ...b, ...patch } : b)));

  const move = (index, delta) => {
    const next = [...blocks];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const remove = (index) => onChange(blocks.filter((_, i) => i !== index));
  const add = (type) => onChange([...blocks, emptyBlock(type)]);

  return (
    <div className="space-y-4" data-testid="block-editor">
      {blocks.map((block, index) => {
        const meta = BLOCK_TYPES.find((t) => t.type === block.type) || BLOCK_TYPES[0];
        const Icon = meta.icon;
        return (
          <div
            key={index}
            className="rounded-2xl border border-black/[0.08] bg-[rgb(var(--background))] p-4"
            data-testid={`block-${index}`}
          >
            <div className="flex items-center gap-2 mb-3">
              <Icon size={16} className="text-primary" />
              <span className="text-xs font-medium uppercase tracking-[0.12em] text-foreground-muted">
                {meta.label}
              </span>
              <div className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  className="p-1.5 rounded-lg hover:bg-black/5 disabled:opacity-30"
                  title="Move up"
                  data-testid={`block-up-${index}`}
                >
                  <ArrowUp size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === blocks.length - 1}
                  className="p-1.5 rounded-lg hover:bg-black/5 disabled:opacity-30"
                  title="Move down"
                  data-testid={`block-down-${index}`}
                >
                  <ArrowDown size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"
                  title="Remove block"
                  data-testid={`block-remove-${index}`}
                >
                  <Trash size={15} />
                </button>
              </div>
            </div>

            {(block.type === 'paragraph' || block.type === 'highlight' || block.type === 'quote') && (
              <textarea
                value={block.text || ''}
                onChange={(e) => update(index, { text: e.target.value })}
                rows={block.type === 'paragraph' ? 5 : 3}
                placeholder={
                  block.type === 'paragraph'
                    ? 'Write the paragraph...'
                    : block.type === 'highlight'
                    ? 'The line you want to stand out'
                    : 'What was said'
                }
                className={textarea}
                data-testid={`block-text-${index}`}
              />
            )}

            {block.type === 'quote' && (
              <Input
                value={block.attribution || ''}
                onChange={(e) => update(index, { attribution: e.target.value })}
                placeholder="Who said it"
                className="mt-2"
                data-testid={`block-attribution-${index}`}
              />
            )}

            {block.type === 'image' && (
              <div className="space-y-2">
                <Input
                  value={block.url || ''}
                  onChange={(e) => update(index, { url: e.target.value })}
                  placeholder="https://image-url..."
                  data-testid={`block-url-${index}`}
                />
                <Input
                  value={block.caption || ''}
                  onChange={(e) => update(index, { caption: e.target.value })}
                  placeholder="Caption (optional)"
                  data-testid={`block-caption-${index}`}
                />
                {block.url && (
                  <div className="rounded-xl overflow-hidden border border-black/[0.06] max-h-48">
                    <img src={block.url} alt="" className="w-full object-cover" />
                  </div>
                )}
              </div>
            )}

            {block.type === 'reference' && (
              <div className="space-y-2">
                <Input
                  value={block.label || ''}
                  onChange={(e) => update(index, { label: e.target.value })}
                  placeholder="Source name, e.g. INE Housing Report 2026"
                  data-testid={`block-label-${index}`}
                />
                <Input
                  value={block.url || ''}
                  onChange={(e) => update(index, { url: e.target.value })}
                  placeholder="https://source-url..."
                  data-testid={`block-url-${index}`}
                />
                <Input
                  value={block.text || ''}
                  onChange={(e) => update(index, { text: e.target.value })}
                  placeholder="Note (optional)"
                  data-testid={`block-note-${index}`}
                />
              </div>
            )}
          </div>
        );
      })}

      <div>
        <Label className="text-xs text-foreground-muted">Add block</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {BLOCK_TYPES.map(({ type, label, icon: Icon }) => (
            <Button
              key={type}
              type="button"
              variant="outline"
              onClick={() => add(type)}
              className="rounded-full h-9 px-4 text-xs border-black/10"
              data-testid={`add-block-${type}`}
            >
              <Icon size={14} className="mr-1.5" />
              {label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
